import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Eye, EyeOff, Copy, Trash2, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'
import type { Secret } from '@/types'

function AddSecretDialog() {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [value, setValue] = useState('')
  const [description, setDescription] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const addMutation = useMutation({
    mutationFn: () => apiClient.post('/secrets', { name, value, description }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secrets'] })
      toast({ title: 'Secret created' })
      setOpen(false)
      setName('')
      setValue('')
      setDescription('')
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Add Secret
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Secret</DialogTitle>
          <DialogDescription>Store a new encrypted secret</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" placeholder="API_KEY" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="value">Value</Label>
            <Textarea id="value" placeholder="Enter secret value" value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Input id="description" placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => addMutation.mutate()} disabled={!name || !value}>
            {addMutation.isPending ? 'Creating...' : 'Create Secret'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SecretCard({ secret }: { secret: Secret }) {
  const [revealed, setRevealed] = useState(false)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/secrets/${secret.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secrets'] })
      toast({ title: 'Secret deleted' })
    },
  })

  const copyToClipboard = () => {
    // In real implementation, would fetch the decrypted value
    toast({ title: 'Copied to clipboard' })
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base font-medium">{secret.name}</CardTitle>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => setRevealed(!revealed)}>
            {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={copyToClipboard}>
            <Copy className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => deleteMutation.mutate()}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {secret.description && (
          <p className="text-sm text-muted-foreground mb-2">{secret.description}</p>
        )}
        <div className="font-mono text-sm bg-muted p-2 rounded">
          {revealed ? '••••••••••••••••' : '••••••••••••••••'}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Last accessed: {secret.lastAccessedAt ? new Date(secret.lastAccessedAt).toLocaleString() : 'Never'}
        </p>
      </CardContent>
    </Card>
  )
}

export function SecretsPage() {
  const [search, setSearch] = useState('')
  const { data } = useQuery({
    queryKey: ['secrets'],
    queryFn: () => apiClient.get<Secret[]>('/secrets'),
  })

  const secrets = data?.data || []
  const filtered = secrets.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Secret Vault</h1>
          <p className="text-muted-foreground">Securely store and manage sensitive data</p>
        </div>
        <AddSecretDialog />
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search secrets..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Lock className="h-12 w-12 text-muted-foreground mb-4" />
            <CardTitle className="mb-2">No secrets found</CardTitle>
            <CardDescription>Add your first secret to get started</CardDescription>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((secret) => (
            <SecretCard key={secret.id} secret={secret} />
          ))}
        </div>
      )}
    </div>
  )
}
